/**
 * V37.34 — Calcul du TTC depuis HT + taxes : fonction PURE utilitaire
 * partagee par tous les simulateurs (RCDS BATICO Annuel, RCDS Nominative,
 * Avenant) et alignee sur la generation PDF backend (Devis ESS).
 *
 * Principe : recalculer chaque taxe en appliquant son `taux` (%) sur le
 * `htFinal` actuel. Si une taxe n'a pas de `taux` mais a un `montant`
 * absolu, on l'utilise tel quel (cas reductions forfaitaires). Si
 * aucune taxe n'est fournie, on applique `fallbackRate` (defaut 0.085
 * pour ESS 8,5%).
 *
 * IMPORTANT : NE PAS appeler de getters qui dependent eux-memes du TTC
 * (-> boucle infinie). Cette fonction prend les arguments necessaires et
 * ne fait que du calcul pur.
 *
 * @param {number} htFinal - Montant HT (deja personnalise si applicable)
 * @param {Array<{taux?: number|string, montant?: number|string}>} taxes
 *   - Liste de taxes. Pour chacune : si `taux` est un nombre, applique
 *     `htFinal * taux / 100` ; sinon utilise `montant` absolu en fallback.
 * @param {number} [fallbackRate=0.085] - Taux de TVA fallback si aucune taxe.
 * @returns {{ttc: number, totalTaxes: number, breakdown: Array}}
 *   - ttc : TTC arrondi a 2 decimales
 *   - totalTaxes : somme des taxes (avant arrondi du TTC)
 *   - breakdown : liste { taux, montant, source: 'taux'|'montant'|'skip' }
 */
export function computeTtcFromHtAndTaxes(htFinal, taxes, fallbackRate = 0.085) {
  const ht = Number(htFinal) || 0;
  if (ht <= 0) return { ttc: 0, totalTaxes: 0, breakdown: [] };

  const breakdown = [];
  let totalTaxes = 0;

  if (Array.isArray(taxes) && taxes.length > 0) {
    for (const t of taxes) {
      if (!t) {
        breakdown.push({ taux: null, montant: 0, source: 'skip' });
        continue;
      }
      const tauxNum = t.taux != null && t.taux !== '' ? Number(t.taux) : NaN;
      const montantNum = t.montant != null && t.montant !== '' ? Number(t.montant) : NaN;
      if (!isNaN(tauxNum)) {
        const m = ht * tauxNum / 100;
        totalTaxes += m;
        breakdown.push({ taux: tauxNum, montant: m, source: 'taux' });
      } else if (!isNaN(montantNum)) {
        totalTaxes += montantNum;
        breakdown.push({ taux: null, montant: montantNum, source: 'montant' });
      } else {
        breakdown.push({ taux: null, montant: 0, source: 'skip' });
      }
    }
  }

  let ttc;
  if (totalTaxes > 0) {
    ttc = Math.round((ht + totalTaxes) * 100) / 100;
  } else {
    const rate = Number(fallbackRate);
    ttc = Math.round(ht * (1 + (isNaN(rate) ? 0.085 : rate)) * 100) / 100;
    totalTaxes = ttc - ht;
  }

  return { ttc, totalTaxes, breakdown };
}

/**
 * Helper : normalise une source de taxes vers le format
 * `{taux?, montant?}[]` accepte par computeTtcFromHtAndTaxes.
 *
 * @param {object} taxesPersonnalisees - { enabled, lignes: [{actif, nom, taux, montant}] }
 * @param {object} resultatTaxes - { detail: [{taux, montant}] } (du backend)
 * @returns {Array}
 */
export function normalizeTaxesSource(taxesPersonnalisees, resultatTaxes) {
  if (taxesPersonnalisees && taxesPersonnalisees.enabled && Array.isArray(taxesPersonnalisees.lignes)) {
    return taxesPersonnalisees.lignes
      .filter((l) => l && l.actif && l.nom)
      .map((l) => ({
        taux: l.taux != null && l.taux !== '' ? Number(l.taux) : null,
        montant: l.montant != null && l.montant !== '' ? Number(l.montant) : null,
      }));
  }
  if (resultatTaxes && Array.isArray(resultatTaxes.detail)) {
    return resultatTaxes.detail.map((t) => ({
      taux: t && t.taux != null && t.taux !== '' ? Number(t.taux) : null,
      montant: t && t.montant != null && t.montant !== '' ? Number(t.montant) : null,
    }));
  }
  return [];
}
